import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { HERO } from "../../config/landing";
import { refreshLandingCopy } from "../../hooks/useLandingCopy";
import { Save, RotateCcw } from "lucide-react";

// Admin page — edit the Landing Page Hero text (headline / sub / cta) directly.
// Values persist to /api/admin/landing-copy and are read live by Welcome.jsx.
// Leaving a field empty = fall back to the built-in default from landing.js.
//
// Fields deliberately match what Welcome.jsx renders:
//   HERO.headline.{en,it}, HERO.sub.{en,it}, HERO.cta.{en,it}
//
// This page does NOT edit images. Landing page images live under
// /admin/images (Assessment Images) using the existing image-override system.

const FIELDS = [
  { key: "headline", label: "Hero headline", helper: 'The big title on the landing page (currently: "Your Hair. Understood.")' },
  { key: "sub",      label: "Hero supporting text", helper: "The sentence directly under the headline." },
  { key: "cta",      label: "Hero CTA button text", helper: 'The main call-to-action button (currently: "Start the Assessment").' },
];

const LANGS = [
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
];

export default function AdminLandingCopy() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copy, setCopy] = useState({});   // { headline: {en, it}, sub: {en, it}, cta: {en, it} }

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/landing-copy`);
      setCopy(r.data?.copy || {});
    } catch {
      toast.error("Could not load landing copy");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (field, lang, value) => {
    setCopy((c) => ({
      ...c,
      [field]: { ...(c[field] || {}), [lang]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/landing-copy`, copy);
      setCopy(r.data?.copy || {});
      await refreshLandingCopy();
      toast.success("Landing copy saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetField = async (field, lang) => {
    // Just clear the override for this one lang; save persists the cleared state.
    const next = { ...copy };
    if (next[field]) {
      const { [lang]: _drop, ...rest } = next[field];
      next[field] = rest;
      if (Object.keys(next[field]).length === 0) delete next[field];
    }
    setCopy(next);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/landing-copy`, next);
      await refreshLandingCopy();
      toast.success("Reverted to default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[#6F6B66]" data-testid="landing-copy-loading">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="admin-landing-copy">
      <div className="mb-6">
        <h1 className="text-2xl text-[#383633]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Landing Page — Hero Text
        </h1>
        <p className="text-sm text-[#6F6B66] mt-2">
          Edit the Hero headline, supporting text and CTA button — in English and Italian.
          Leave any field empty to revert it to the built-in default.
        </p>
      </div>

      <div className="space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key} className="rounded-2xl border kt-line bg-white p-5" data-testid={`copy-field-${f.key}`}>
            <div className="mb-4">
              <div className="text-sm text-[#383633] font-medium">{f.label}</div>
              <div className="text-xs text-[#6F6B66] mt-1">{f.helper}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {LANGS.map((l) => {
                const overrideVal = copy[f.key]?.[l.code] || "";
                const defaultVal = HERO[f.key]?.[l.code] || "";
                const hasOverride = overrideVal !== "";
                return (
                  <div key={l.code}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] kt-tracking uppercase text-[#6F6B66]">{l.label}</label>
                      {hasOverride && (
                        <button
                          onClick={() => resetField(f.key, l.code)}
                          disabled={saving}
                          data-testid={`reset-${f.key}-${l.code}`}
                          className="text-[11px] text-[#6F6B66] hover:text-[#383633] inline-flex items-center gap-1"
                          title={`Revert to default: "${defaultVal}"`}
                        >
                          <RotateCcw className="h-3 w-3" /> Reset
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={overrideVal}
                      onChange={(e) => setField(f.key, l.code, e.target.value)}
                      placeholder={defaultVal}
                      data-testid={`input-${f.key}-${l.code}`}
                      className="w-full rounded-xl border kt-line bg-white px-3 py-2 text-sm text-[#383633] focus:outline-none focus:ring-2 focus:ring-[#D8B589]"
                    />
                    <div className="text-[10px] text-[#6F6B66] mt-1">
                      Default: <span className="italic">{defaultVal}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-landing-copy"
            className="inline-flex items-center gap-2 rounded-full bg-[#383633] text-white px-6 py-2.5 text-sm hover:bg-black disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
          <span className="text-xs text-[#6F6B66]">
            Changes appear on the public landing page immediately after saving.
          </span>
        </div>
      </div>
    </div>
  );
}
